import { useEffect, useState } from "react";
import { BADGE_TYPE, REVERSE_MAPING_BADGE_TYPE, ICON_COMPLETE_MAP } from "../../RnRCommonComponent/CoreBadge/rewardsConstants";
import { message } from "antd";
import { useRnRCommonService } from "../../RnRCommonComponent/service/RnRCommon.service.jsx";
import { useManageRewardsService } from "../../manageRewards/services/manageRewards.service.jsx";
import { useNominatedRewardsService } from "../service/NominatedRewards.service.jsx";
import gql from 'graphql-tag';
import { withApollo } from 'react-apollo';

// GraphQL query to fetch badges by company ID
const QUERY_BADGES_BY_COMPANY_ID = gql`
  query QueryBadgesByCompanyIdIndex($companyId: ID!, $limit: Int, $nextToken: String) {
    queryBadgesByCompanyIdIndex(companyId: $companyId, limit: $limit, nextToken: $nextToken) {
      items {
        id
        companyId
        badgeTypeId
        assignmentSSORequiredId
        recipientPayTypeId
        recipientSSORequiredId
        physicalAwardActivityId
        iconId
        snappyGrantId
        snappyGrant {
          id
          snappyCampaignId
          name
          description
          costInCurrency
        }
        name
        label
        description
        image
        color
        gradientColor
        points
        bonusPoints
        rewardBonus
        rewardMaxBonus
        assignmentLimit
        assignmentLevelId
        recipientLevelId
        isPreApproved
        isRewardRequiresDate
        visibility
        isAssignmentSSORequired
        isRecipientSSORequired
        automationRule
        externalWorkFlowId
      }
      nextToken
    }
  }
`;

// The hook is being called without arguments in ManageNominatedRewardContainer.jsx
// So we need to handle the case when client is not passed
const useManageNominatedReward = (props = {}) => {
    const { client } = props;
    const [badgeList, setBadgeList] = useState([])
    const [empLoginType, setEmpLoginType] = useState([])
    const [empPayType, setEmpPayType] = useState([])
    const [empLevel, setEmpLevel] = useState([])
    const [showLoader, setShowLoader] = useState({
        badgeList: false,
        empLoginType: false,
        empPayType: false,
        empLevel: false,
    })
    const commonAPIService = useRnRCommonService();
    const manageRewardsService = useManageRewardsService();
    const nominatedAPIService = useNominatedRewardsService();

    const getAllBadgeList = async () => {
        setShowLoader({
            badgeList: true,
            empLoginType: true,
            empPayType: true,
            empLevel: true,
        });
        
        try {
            // Check if client is available
            if (!client) {
                console.error('Apollo client not available');
                setShowLoader({
                    ...showLoader,
                    badgeList: false
                });
                return;
            }
            
            // Get current user from localStorage since it might not be in props
            const currentUserStr = localStorage.getItem('currentUser');
            const currentUser = currentUserStr ? JSON.parse(currentUserStr) : null;
            const companyId = currentUser?.companyId || currentUser?.company?.id;
            
            if (!companyId) {
                console.error('Company ID not found');
                setShowLoader({
                    ...showLoader,
                    badgeList: false
                });
                return;
            }
            
            // Use GraphQL to fetch badges
            const { data, error } = await client.query({
                query: QUERY_BADGES_BY_COMPANY_ID,
                variables: {
                    companyId: companyId,
                    limit: 100,
                    nextToken: null
                },
                fetchPolicy: 'network-only'
            });
            
            if (error) {
                console.error('Error fetching badges by company ID:', error);
                message.error('Failed to fetch recognitions list! Try again later.');
                setShowLoader({
                    ...showLoader,
                    badgeList: false
                });
                return;
            }
            
            if (data?.queryBadgesByCompanyIdIndex?.items) {
                // Process the GraphQL response
                const enrichedBadges = data.queryBadgesByCompanyIdIndex.items.map(badge => {
                    const badgeTypeCode = REVERSE_MAPING_BADGE_TYPE[badge.badgeTypeId] || '';
                    
                    let iconInfo = null;
                    if (badge.iconId) {
                        iconInfo = ICON_COMPLETE_MAP.find(icon => icon.id === badge.iconId);
                    }
                    
                    return {
                        ...badge,
                        badgeType: badgeTypeCode,
                        iconInfo
                    };
                });
                
                // Filter badges by type - use the same pattern as in useGetBadgeList.js
                console.log('Badge types available:', enrichedBadges.map(b => b.badgeType));
                const filteredBadges = enrichedBadges.filter(badge =>
                    badge.badgeType === "NOMINATED" &&
                    (badge.visibility !== false) // Only show visible badges
                );
                console.log(`Found ${filteredBadges.length} nominated badges`);
                setBadgeList(filteredBadges);
            } else {
                console.error('No items in GraphQL response');
            }
        } catch (error) {
            console.error('Error fetching badges:', error);
            message.error('Failed to fetch recognitions list! Try again later.');
        } finally {
            setShowLoader({
                ...showLoader,
                badgeList: false
            });
        }

        manageRewardsService.getEmployeeLevels().then((response) => {
            if (response?.code === 200) {
                if (response?.data) {
                    setEmpLevel(response.data)
                }
            } else {
                message.error('Failed to fetch recognitions list! Try again later.');
            }
        }).catch(() => {
            message.error('Failed to fetch recognitions list! Try again later.');
        }).finally(() => {
            setShowLoader({
                ...showLoader,
                empLevel: false
            })
        });

        nominatedAPIService.getEmployeeLoginType().then((response) => {
            if (response?.code === 200) {
                if (response?.data) {
                    setEmpLoginType(response.data)
                }
            } else {
                message.error('Failed to fetch recognitions list! Try again later.');
            }
        }).catch(() => {
            message.error('Failed to fetch recognitions list! Try again later.');
        }).finally(() => {
            setShowLoader({
                ...showLoader,
                empLoginType: false
            })
        });

        manageRewardsService.getEmployeePayType().then((response) => {
            if (response?.code === 200) {
                if (response?.data) {
                    setEmpPayType(response.data)
                }
            } else {
                message.error('Failed to fetch recognitions list! Try again later.');
            }
        }).catch(() => {
            message.error('Failed to fetch recognitions list! Try again later.');
        }).finally(() => {
            setShowLoader({
                ...showLoader,
                empPayType: false
            })
        });
    };

    const createNominationConfiguration = (values) => {
        const nominationConfigObject = {
            id: values?.id,
            badgeId: values?.badgeId,
            startDate: values?.startDate,
            endDate: values?.endDate,
            status: values?.status,
            awardedCount: parseInt(values?.nominationCriteria?.nominator?.awardedCount),
            nomineeEmployeeLevelId: values?.nominationCriteria?.nominee?.employeeLevelId,
            nomineeEmployeelogInTypeId: values?.nominationCriteria?.nominee?.employeelogInTypeId,
            nomineeEmployeePayTypeId: values?.nominationCriteria?.nominee?.employeePayTypeId,
            nominatorEmployeeLevelId: values?.nominationCriteria?.nominator?.employeeLevelId,
            nominatorEmployeelogInTypeId: values?.nominationCriteria?.nominator?.employeelogInTypeId,
            nominatorEmployeePayTypeId: values?.nominationCriteria?.nominator?.employeePayTypeId,
        }
        return nominatedAPIService.createNominationConfiguration(nominationConfigObject);
    }

    const getNominationConfigurationById = (id) => {
        return commonAPIService.getNominationConfigurationById(id);
    }

    // Use useEffect to fetch data on mount
    useEffect(() => {
        getAllBadgeList();
    }, []); // Only run once on mount

    return ({
        empLevel,
        badgeList,
        empLoginType,
        empPayType,
        loading: Object.values(showLoader).flat().includes(true),
        createNominationConfiguration,
        getNominationConfigurationById
    })
}

export default withApollo(useManageNominatedReward)